import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  Divider,
  Badge
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  Person,
  LocationOn
} from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

// Set Italian locale
dayjs.locale('it');

const ReservationCalendar = ({ reservations }) => {
  const [selectedDate, setSelectedDate] = useState(dayjs());

  // Group reservations by date
  const reservationsByDate = useMemo(() => {
    const grouped = {};
    reservations.forEach(reservation => {
      const date = dayjs(reservation.date).format('YYYY-MM-DD');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(reservation);
    });
    return grouped;
  }, [reservations]);

  // Get reservations for selected date
  const selectedDateReservations = useMemo(() => {
    const dateKey = selectedDate.format('YYYY-MM-DD');
    return reservationsByDate[dateKey] || [];
  }, [selectedDate, reservationsByDate]);

  // Get all dates with reservations for calendar highlighting
  const datesWithReservations = useMemo(() => {
    return new Set(Object.keys(reservationsByDate));
  }, [reservationsByDate]);

  // Get reservations for a specific date
  const getReservationsForDate = (date) => {
    const dateKey = date.format('YYYY-MM-DD');
    return reservationsByDate[dateKey] || [];
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      cancelled: 'default',
      completed: 'info'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'In attesa',
      approved: 'Approvata',
      rejected: 'Rifiutata',
      cancelled: 'Annullata',
      completed: 'Completata'
    };
    return labels[status] || status;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return days[dayOfWeek];
  };

  // Custom day component for calendar
  const CustomDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const hasReservations = datesWithReservations.has(day.format('YYYY-MM-DD'));
    const isToday = dayjs().isSame(day, 'day');
    const isSelected = selectedDate && dayjs(selectedDate).isSame(day, 'day');
    const reservationCount = getReservationsForDate(day).length;

    return (
      <Box sx={{ position: 'relative' }}>
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          sx={{
            backgroundColor: isToday ? 'primary.light' : 
                           isSelected ? 'secondary.light' : 
                           'transparent',
            border: isToday ? '2px solid primary.main' : 
                    isSelected ? '2px solid secondary.main' : 
                    '1px solid transparent',
            '&:hover': {
              backgroundColor: 'action.hover',
              transform: 'scale(1.02)',
              transition: 'all 0.2s'
            }
          }}
        />
        {hasReservations && (
          <Badge 
            badgeContent={reservationCount} 
            sx={{
              position: 'absolute',
              top: 7,
              right: 7,
              '& .MuiBadge-badge': {
                fontSize: '0.6rem',
                height: '19px',
                backgroundColor: 'primary.main',
                color: 'white',
                fontWeight: 'bold'
              }
            }}
          />
        )}
      </Box>
    );
  };

  if (reservations.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CalendarToday sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nessuna prenotazione trovata
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Modifica i filtri per vedere più risultati
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Calendar */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
            Calendario Prenotazioni
          </Typography>
          
          <DateCalendar
            value={selectedDate}
            onChange={setSelectedDate}
            slots={{ day: CustomDay }}
            showDaysOutsideCurrentMonth={true}
            sx={{
              width: '100%',
              '& .MuiPickersCalendarHeader-root': {
                marginBottom: 1
              }
            }}
          />
        </Paper>
      </Grid>

      {/* Reservation Agenda */}
      <Grid  size={{ xs: 12, md: 8 }}>
        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>
            {getDayName(selectedDate.day())} {selectedDate.format('DD MMMM YYYY')}
          </Typography>
          
          {selectedDateReservations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CalendarToday sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nessuna prenotazione per questa data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Seleziona un'altra data dal calendario per visualizzare le prenotazioni
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
              {selectedDateReservations
                .sort((a, b) => a.startTime.localeCompare(b.startTime))
                .map((reservation) => (
                <Card 
                  key={reservation._id} 
                  variant="outlined" 
                  sx={{ 
                    mb: 2, 
                    borderLeft: `4px solid ${getStatusColor(reservation.status) === 'success' ? '#4caf50' : 
                                                      getStatusColor(reservation.status) === 'warning' ? '#ff9800' :
                                                      getStatusColor(reservation.status) === 'error' ? '#f44336' : '#9e9e9e'}`
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" component="div" gutterBottom>
                          {reservation.reservable?.name || 'N/A'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            label={getStatusLabel(reservation.status)}
                            color={getStatusColor(reservation.status)}
                            size="small"
                          />
                          <Chip
                            label={`${reservation.totalPrice}€`}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      </Box>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                        {reservation.startTime} - {reservation.endTime}
                      </Typography>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid  size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Person sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {reservation.user?.name} {reservation.user?.surname}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid  size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <AccessTime sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Durata: {formatDuration(reservation.duration)}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid  size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <LocationOn sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {reservation.reservable?.location || 'Non specificata'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid  size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CalendarToday sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Creata: {dayjs(reservation.createdAt).format('HH:mm')}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    {reservation.notes && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Note:</strong> {reservation.notes}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ReservationCalendar;
